import { Star, MapPin, Clock, ChevronRight } from "lucide-react";
import type { StorefrontStore } from "@/types/storefront";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

interface StoreCardProps {
  store: StorefrontStore;
  index?: number;
}

const StoreCard = ({ store, index = 0 }: StoreCardProps) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05, duration: 0.4 }}
      className="h-full"
    >
      <Link
        to={`/stores/${store.id}`}
        className="group block bg-card rounded-2xl border border-border/50 overflow-hidden hover:shadow-lg transition-all duration-300 hover:-translate-y-1 h-full flex flex-col"
      >
        <div className="relative h-28 bg-secondary/35 flex items-center justify-center p-3 overflow-hidden border-b border-border/30 shrink-0">
          <img
            src={store.image}
            alt={store.name}
            className="max-h-full max-w-full object-contain rounded-lg group-hover:scale-105 transition-transform duration-500 z-10"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/5 to-transparent pointer-events-none" />
          <div className="absolute top-2 left-2 z-20">
            <span
              className={`text-[9px] font-bold px-1.5 py-0.5 rounded-md ${
                store.isOpen
                  ? "bg-success text-success-foreground"
                  : "bg-destructive text-destructive-foreground"
              }`}
            >
              {store.isOpen ? "Open" : "Closed"}
            </span>
          </div>
          <div className="absolute top-2 right-2 z-20">
            <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-md bg-card/95 text-foreground border border-border/20 shadow-sm">
              {store.productCount} prod
            </span>
          </div>
        </div>

        <div className="p-3 flex-1 flex flex-col justify-between">
          <div>
            <div className="flex items-start justify-between gap-1 mb-0.5">
              <h3 className="font-heading font-bold text-sm line-clamp-1 group-hover:text-primary transition-colors leading-tight">{store.name}</h3>
              <div className="flex items-center gap-0.5 shrink-0">
                <Star className="w-3.5 h-3.5 fill-warning text-warning" />
                <span className="text-xs font-semibold">{store.rating.toFixed(1)}</span>
              </div>
            </div>

            <p className="text-[11px] text-muted-foreground line-clamp-1 mb-2.5 leading-normal">{store.description}</p>
          </div>

          <div className="flex items-center justify-between text-[11px] text-muted-foreground border-t border-border/30 pt-2 mt-auto">
            <span className="flex items-center gap-0.5">
              <MapPin className="w-3 h-3 text-primary" />
              {store.distance}
            </span>
            <span className="flex items-center gap-0.5">
              <Clock className="w-3 h-3 text-primary" />
              {store.deliveryTime}
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export default StoreCard;
